// import axios from 'axios';
import { Config } from '../config';
import { StatusCodeFilter } from './StatusCodeError'; // StatusCodeError, StatusCodeFilterAxios
import { isAuthSuccess, isGetTokenSuccess, isTokenExpired } from './Messages/AuthSuccess';
import { TypedStorage } from '../Utils/TypedStorage';
import { IResults } from '../Store/User/Types';
import { isGetError, isGetMessageSuccess, isGetSuccess } from './Messages/callSuccess';
import { IAdvancedSearchPlayerFilters, IEmissionSearchFilters, IPlayerRapidSearchFilters, playerPoliticalGroupsFilters, playerPoliticalPersonFilters } from '../Store/Player/Types';
import { IExportFilters } from '../Store/Export/Types';
import { IStatsEvolutionFilters, IStatsFilters2, IStatsFilters3, IStatsFilters4, IStatsFilters5 } from '../Store/Statistics/Types';
import { IFiltersDetail } from '../Store/Dashboard/Types';
import { IElectionDetailFilters, IElectionFilters, IElectionsStudyFilters, PRESIDENTIEL_ELECTION } from '../Store/Elections/Types';
import jwt from 'jwt-decode';
import { diff_minutes } from '../Utils/Time';
import { ensureEndAfterStartDay } from '../Utils/Time';

/**
 * Backend online statuses
 */
export enum OnlineStatus {
    // Backend is connecting
    CONNECTING = 1,
    // Backend is connected
    ONLINE,
    // Backend is offline
    OFFLINE,
}

/**
 * Backend allowed methods
 */
export type BackendMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';


/**
 * Backend handles all backend operations.
 */
export class Backend {
    public static getInstance() {
        if (!Backend.instance) {
            Backend.instance = new Backend();
        }
        return Backend.instance;
    }

    private static instance: Backend;

    private token: string = '';
    public setToken(tok: string) {
        this.token = tok;
    }

    private handleTokenExpired() {
        TypedStorage.set('userName', '');
        TypedStorage.set('userToken', '');
        TypedStorage.set('userDate', 0);
        TypedStorage.set('medias', '');
        window.location.reload();
    }

    private async callBackend(
        route: string,
        method: BackendMethod = 'GET',
        body: ArrayBuffer | FormData | null = null,
        addHeaders: Headers | null = null
    ): Promise<ArrayBuffer> {
        let url = ((route.length === 0) || (route[0] !== '/')) ? `${Config.backendAPIURL}/${route}` : `${Config.backendAPIURL}${route}`;
        if (this.token !== "" && this.tokenExpiresSoon(this.token)) {
            this.refreshToken(this.token);
        }
        const headers = (addHeaders === null) ?
            new Headers({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            }) : addHeaders;
        return fetch(
            url,
            {
                body,
                cache: 'no-cache',
                headers,
                method,
                mode: 'cors',
                redirect: 'error',
            },
        ).then(StatusCodeFilter);
    }

    // http call to backend wrapper as JSON post
    private async callBackendJSON(
        route: string,
        obj: any,
        addHeaders: Headers | null = null
    ): Promise<ArrayBuffer> {
        const data = this.obj2ab(obj);
        const headers = (addHeaders === null) ? new Headers() : addHeaders;
        headers.append('content-type', 'application/json');
        headers.append('Access-Control-Allow-Origin', '*');
        return this.callBackend(route, 'POST', data, headers);
    }

    private async callBackendNoStatusCodeFilter(
        route: string,
        method: BackendMethod = 'GET',
        body: ArrayBuffer | FormData | null = null,
        addHeaders: Headers | null = null,
    ): Promise<Response> {
        const url = ((route.length === 0) || (route[0] !== '/')) ?
            `${Config.backendAPIURL}/${route}` :
            `${Config.backendAPIURL}${route}`;

        const headers = (addHeaders === null) ?
            (this.token ?
                new Headers({
                    'Authorization': 'Token ' + this.token,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }) :
                new Headers({
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                })
            ) : addHeaders;

        return fetch(
            url,
            {
                body,
                cache: 'no-cache',
                headers,
                method,
                mode: 'cors',
                redirect: 'error',
                credentials: 'include',
            },
        ).then((res: Response) => {
            return res
        });
    }

    private tokenExpiresSoon(token: any) {
        let token_decoded: any = jwt(token);
        if (token_decoded.hasOwnProperty('exp')) {
            let expires: any = token_decoded['exp'];

            const expiration_date = new Date(expires * 1000);
            const now: any = new Date();
            const diff = diff_minutes(expiration_date, now);
            if ((diff > 0 && diff <= 15) || (now > expiration_date)) {
                return true
            }
        } return false;
    }

    private async callBackendRefreshToken(
        route: string,
        method: BackendMethod = 'GET',
        body: ArrayBuffer | FormData | null = null,
        addHeaders: Headers | null = null
    ): Promise<ArrayBuffer> {
        const url = ((route.length === 0) || (route[0] !== '/')) ? `${Config.backendAPIURL}/${route}` : `${Config.backendAPIURL}${route}`;

        const headers = (addHeaders === null) ?
            new Headers({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            }) : addHeaders;
        return fetch(
            url,
            {
                body,
                cache: 'no-cache',
                headers,
                method,
                mode: 'cors',
                redirect: 'error',
            },
        ).then((res: Response) => {
            return res.arrayBuffer()
        });
    }
    private refreshToken(token: string) {
        const headers = new Headers();
        headers.append('content-type', 'application/json');

        const url = '/refresh_token';
        return this.callBackendRefreshToken(
            url,
            'POST',
            this.obj2ab({ token }),
            headers

        )
            .then((res: any) => {
                return this.ab2obj(res)
            }).then((data: any) => {
                if (data.hasOwnProperty('token')) {
                    this.setToken(data['token'])
                }
            });
    }

    public async createSchema(token: string) {
        return this.callBackendJSON(
            '/create_schema',
            {
                token
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid create_schema: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async login(username: string, password: string) {
        return this.callBackendJSON(
            '/login_check',
            { username, password },
            null
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isAuthSuccess(res)) {
                    let results: IResults = res.results as any
                    this.token = results.token;

                    return [results.token, results.expires * 1000, results.media_list, results.user, results.have_right_show_total];
                } else if (isGetError(res)) {
                    throw new Error('Invalid Login: ' + res.results);
                }
                throw new Error('Invalid response from backend, Login');
            });
    }

    public async getDateCloture(token: string) {
        return this.callBackendJSON(
            '/getDateCloture',
            { token },
            null
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getDateCloture: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getPoliticalRegime(token: string) {
        return this.callBackendJSON(
            '/get_political_regime',
            {
                token
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPoliticalRegime: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }

    public async getPoliticalGroup(token: string, political_regime_id?: number) {
        return this.callBackendJSON(
            '/get_political_group',
            {
                token,
                political_regime_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPoliticalGroup: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }

    public async getMediasTDP(token: string) {
        return this.callBackendJSON(
            '/get_medias_tdp',
            {
                token
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getMediasTDP: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getAuthorizedMedias(token: string) {
        return this.callBackendJSON(
            '/getAuthorizedMedias',
            {
                token
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res.results;
                } else if (isGetMessageSuccess(res)) {
                    return res.message
                } else if (isGetError(res)) {
                    throw new Error('Invalid getAuthorizedMedias ');
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getProgramTypes(token: string) {
        return this.callBackendJSON(
            '/get_program_types',
            {
                token
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getMediasTypes: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getProgramEditors(token: string) {
        return this.callBackendJSON(
            '/get_program_editors',
            {
                token
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getMediasEditors: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getDashboard(token: string, media_ids: number[], program_type_ids: number[], program_editor_ids: number[], time_slot_start: string, time_slot_end: string, date_type: string, date_start: Date, date_end: Date, program_name?: string) {
        return this.callBackendJSON(
            '/get_dashboard',
            {
                token,
                media_ids,
                program_type_ids,
                program_editor_ids,
                time_slot_start,
                time_slot_end,
                date_type,
                date_start: date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(date_start, date_end).toISOString().replace('.000Z', ''),
                program_name
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getDashboard: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getDashboardDetail(filters: IFiltersDetail) {
        let token = TypedStorage.get('userToken', '');
        return this.callBackendJSON(
            '/get_dashboard_detail',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                political_group_ids: filters.political_group_ids,
                political_regime_ids: filters.political_regime_ids,
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id,
                program_name: filters.program_name
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getDashboardDetail: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getTokenPlayer(music: boolean, readjusted: boolean, username: string) {
        return this.callBackendJSON(
            '/getTokenPlayer',
            {
                music: music,
                readjusted: readjusted,
                username: username
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetTokenSuccess(res)) {
                    return res;
                } else if (isTokenExpired(res)) {
                    this.handleTokenExpired();
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getAutocompletePerson(token: string, person_name: string) {
        return this.callBackendJSON(
            '/autocomplet_person',
            {
                token,
                person_name
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getAutocompletePerson: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getPlayerRapidSearch(filters: IPlayerRapidSearchFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/player_rapid_search',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                person_name: filters.person_name,
                date_start: filters.start_date.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.start_date, filters.end_date).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPlayerRapidSearch: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getPdfPlayerRapidSearch(filters: IPlayerRapidSearchFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/pdf_player_rapid_search',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                person_name: filters.person_name,
                date_start: filters.start_date.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.start_date, filters.end_date).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPDFPlayerRapidSearch: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getXlsPlayerRapidSearch(filters: IPlayerRapidSearchFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_player_rapid_search',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                person_name: filters.person_name,
                date_start: filters.start_date.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.start_date, filters.end_date).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getXlsPlayerRapidSearch: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getPlayerPoliticalGroups(filters: playerPoliticalGroupsFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_player_political_groups',
            {
                token,
                media_ids: filters.media_ids,
                date_start: filters.start_date.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.start_date, filters.end_date).toISOString().replace('.000Z', ''),
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPlayerPoliticalGroups: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getPlayerPersons(filters: playerPoliticalPersonFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_player_persons',
            {
                token,
                media_ids: filters.media_ids,
                political_group_id: filters.political_group_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPlayerPersons: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getPlayerAdvancedSearch(filters: IAdvancedSearchPlayerFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/player_advanced_search',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                person_id: filters.person_id,
                political_group_id: filters.political_group_id,
                date_start: filters.start_date.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.start_date, filters.end_date).toISOString().replace('.000Z', ''),
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                study_type_id: filters.study_type_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPlayerAdvancedSearch: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatisticsStep1(token: string, media_ids: number[], program_type_ids: number[], date_start: Date, date_end: Date, show_pres: boolean) {
        return this.callBackendJSON(
            '/statistics_distribution_step_1',
            {
                token,
                media_ids,
                program_type_ids,
                date_start: date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(date_start, date_end).toISOString().replace('.000Z', ''),
                show_pres
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatisticsStep1: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatisticsStep2(filters: IStatsFilters2, total_duration_s: number) {
        let token = TypedStorage.get('userToken', '');
        return this.callBackendJSON(
            '/statistics_distribution_step_2',
            {
                token,
                political_regime_id: filters.political_regime_id,
                date_start: filters.startDate.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.startDate, filters.endDate).toISOString().replace('.000Z', ''),
                media_ids: filters.mediasIds,
                program_type_ids: filters.programTypesIds,
                total_duration_s: total_duration_s
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatisticsStep2: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatisticsStep3(filters: IStatsFilters3) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/statistics_distribution_step_3',
            {
                token,
                political_group_id: filters.political_group_id,
                date_start: filters.startDate.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.startDate, filters.endDate).toISOString().replace('.000Z', ''),
                program_type_ids: filters.programTypesIds,
                media_ids: filters.mediasIds
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatisticsStep3: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatisticsStep4(filters: IStatsFilters4) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/statistics_distribution_step_4',
            {
                token,
                person_id: filters.person_id,
                political_group_id: filters.political_group_id,
                date_start: filters.startDate.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.startDate, filters.endDate).toISOString().replace('.000Z', ''),
                media_ids: filters.mediasIds,
                program_type_ids: filters.programTypesIds
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatisticsStep4: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatisticsStep5(filters: IStatsFilters5) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/statistics_distribution_step_5',
            {
                token,
                media_id: filters.media_id,
                program_type_ids: filters.program_type_ids,
                political_regime_id: filters.political_regime_id,
                political_group_id: filters.political_group_id,
                person_id: filters.person_id,
                date_start: filters.startDate.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.startDate, filters.endDate).toISOString().replace('.000Z', '')
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatisticsStep4: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getMediasSource(token: string, media_ids: number[]) {
        return this.callBackendJSON(
            '/get_media_source',
            {
                token,
                media_ids,
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getMediaSource: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getPoliticalGroupSpecific(token: string, year: number) {
        return this.callBackendJSON(
            '/get_political_group_specific',
            {
                token,
                year,
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getPoliticalGroupSpecific: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatsEvolutionPerMonth(token: string, year: number, media_ids: number[], program_type_ids: number[], political_group_ids: number[]) {
        return this.callBackendJSON(
            '/statistics_evolution_by_month',
            {
                token,
                year,
                media_ids,
                program_type_ids,
                political_group_ids
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatsEvolutionPerMonth: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatsEvolutionPdf(filters: IStatsEvolutionFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/pdf_statistics_evolution',
            {
                token,
                year: filters.year,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                political_group_ids: filters.political_group_ids
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatsEvolutionPdf: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getStatsEvolutionXls(filters: IStatsEvolutionFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_statistics_evolution',
            {
                token,
                year: filters.year,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                political_group_ids: filters.political_group_ids
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getStatsEvolutionXls: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async generatePdfFile(filters: IExportFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/pdf_dashboard',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                date_type: filters.date_type,
                display: filters.display,
                show_detail_list: filters.show_detail_list,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                program_name: filters.program_name
            },
            new Headers({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            })
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isTokenExpired(res)) {
                    this.handleTokenExpired();
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async generateExcelFile(filters: IExportFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_dashboard',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                date_type: filters.date_type,
                display: filters.display,
                show_detail_list: filters.show_detail_list,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                program_name: filters.program_name
            }
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isTokenExpired(res)) {
                    this.handleTokenExpired();
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async generateXlsDashboarLegislativedDetails(filters: IFiltersDetail) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_dashboard_legislative_detail',
            {
                token,
                media_ids: filters.media_ids,
                political_group_ids: filters.political_group_id ? [filters.political_group_id] : (filters.political_group_ids || []),
                political_regime_ids: filters.political_regime_ids || [],
                program_type_ids: filters.program_type_ids || [],
                program_editor_ids: filters.program_editor_ids || [],
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            },
            new Headers({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            })
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isTokenExpired(res)) {
                    this.handleTokenExpired();
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async generatePdfDashboardLegislativeDetails(filters: IFiltersDetail) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/pdf_dashboard_legislative_detail',
            {
                token,
                media_ids: filters.media_ids,
                political_group_ids: filters.political_group_id ? [filters.political_group_id] : (filters.political_group_ids || []),
                political_regime_ids: filters.political_regime_ids || [],
                program_type_ids: filters.program_type_ids || [],
                program_editor_ids: filters.program_editor_ids || [],
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            },
            new Headers({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            })
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isTokenExpired(res)) {
                    this.handleTokenExpired();
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async generateXlsDashboardDetails(filters: IFiltersDetail) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_dashboard_detail',
            {
                token,
                media_ids: filters.media_ids,
                political_group_ids: filters.political_group_id ? [filters.political_group_id] : (filters.political_group_ids || []),
                political_regime_ids: filters.political_regime_ids || [],
                program_type_ids: filters.program_type_ids || [],
                program_editor_ids: filters.program_editor_ids || [],
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            }
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isTokenExpired(res)) {
                    this.handleTokenExpired();
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async generatePdfDashboardDetails(filters: IFiltersDetail) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/pdf_dashboard_detail',
            {
                token,
                media_ids: filters.media_ids,
                political_group_ids: filters.political_group_id ? [filters.political_group_id] : (filters.political_group_ids || []),
                political_regime_ids: filters.political_regime_ids || [],
                program_type_ids: filters.program_type_ids || [],
                program_editor_ids: filters.program_editor_ids || [],
                time_slot_start: filters.time_slot_start,
                time_slot_end: filters.time_slot_end,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            },
            new Headers({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*'
            })
        )
            .then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isTokenExpired(res)) {
                    this.handleTokenExpired();
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getAutocompleteEmission(emission_name: string) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/autocomplet_emission',
            {
                token,
                emission_name
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getAutocompleteEmission: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getAutocompleteEmissionByMedia(emission_name: string, media_ids: number[]) {
        let token = TypedStorage.get('userToken', '');
    
        return this.callBackendJSON(
            '/autocomplet_emission_by_media',
            {
                token,
                emission_name,
                media_ids
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getAutocompleteEmissionByMedia: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async playerEmissionSearch(filters: IEmissionSearchFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/player_emission_search',
            {
                token,
                media_ids: filters.media_ids,
                program_type_ids: filters.program_type_ids,
                program_editor_ids: filters.program_editor_ids,
                emission_name: filters.emission_name,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                study_type_id: filters.study_type_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getAutocompleteEmission: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    // elections
    public async getTimeAllocation(filters: IElectionDetailFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_time_allocation',
            {
                token,
                detail_etude_id: filters.details_etude_id,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_time_allocation: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getTimeAllocationLegislative(filters: IElectionFilters) {
        let token = TypedStorage.get('userToken', '');
        if (filters.study_type_id === 40) {
            filters.is_nat = false;
        }
        return this.callBackendJSON(
            '/get_time_allocation_legislative_v2',
            {
                token,
                is_nat: filters.is_nat,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start,
                detail_etude_ids: filters.detail_etude_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_time_allocation: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getDetailEtudeIds(study_type_id: number) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_detail_etude_ids',
            {
                token,
                study_type_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_detail_etude_ids: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getHomeStudyType(filters: IElectionsStudyFilters) {
        let token = TypedStorage.get('userToken', '');
        let univers: any = undefined
        let limit: any = undefined
        if (filters !== undefined) {
            univers = filters.univers
            limit = filters.limit
        }
        return this.callBackendJSON(
            '/get_home_study_type',
            {
                token,
                univers,
                limit
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_home_study_type: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getCircoList(study_type_id: number, is_nat: boolean, media_ids: number[]) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_constituency',
            {
                token,
                study_type_id,
                is_nat,
                media_ids
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                return res;
            });
    }
    public async getDashboardElection(filters: IElectionFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_dashboard_election',
            {
                token,
                is_nat: filters.is_nat,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_dashboard_election: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getDashboardCirconscription(filters: IElectionFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_dashboard_circonscription',
            {
                token,
                is_nat: filters.is_nat,
                study_type_id: filters.study_type_id,
                detail_etude_id: filters.detail_etude_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_dashboard_election: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getXlsDashboardElectionDetail(filters: IElectionDetailFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_dashboard_election_detail',
            {
                token,
                is_nat: filters.is_nat,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_xls_dashboard: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getXlsDashboardElection(filters: IElectionDetailFilters) {
        let token = TypedStorage.get('userToken', '');
        let url_base = '/xls_dashboard_election';
        if (filters.study_type_id >= PRESIDENTIEL_ELECTION.FIRST_ROUND) {
            url_base = '/xls_time_allocation'
        }
        return this.callBackendJSON(
            url_base,
            {
                token,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start,
                detail_etude_id: filters.details_etude_id
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_xls_dashboard_election: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getXlsDashboardLegislativ(filters: IElectionDetailFilters) {
        let token = TypedStorage.get('userToken', '');
        let url_base = '/xls_time_allocation_legislative';

        return this.callBackendJSON(
            url_base,
            {
                token,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start,
                is_nat: filters.is_nat,
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid getXlsDashboardLegislativ: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getXlsDashboardCirconscription(filters: IElectionDetailFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_dashboard_circonscription',
            {
                token,
                is_nat: filters.is_nat,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_xls_dashboard_circonscription: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getDashboardElectionDetail(filters: IElectionDetailFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_dashboard_election_detail',
            {
                token,
                is_nat: filters.is_nat,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
                program_editor_ids: filters.program_editor_ids,
                program_type_ids: filters.program_type_ids,
                time_slot_end: filters.time_slot_end,
                time_slot_start: filters.time_slot_start,
                detail_etude_id: filters.detail_etude_id,
                political_group_id: filters.political_group_id,
                person_function: filters.person_function,
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_dashboard_election_detail: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getXlsDeclarationCSA(filters: IElectionFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/xls_declaration_csa',
            {
                token,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid xls_declaration_csa: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    public async getDeclarationCSA(filters: IElectionFilters) {
        let token = TypedStorage.get('userToken', '');

        return this.callBackendJSON(
            '/get_declaration_csa',
            {
                token,
                study_type_id: filters.study_type_id,
                date_start: filters.date_start.toISOString().replace('.000Z', ''),
                date_end: ensureEndAfterStartDay(filters.date_start, filters.date_end).toISOString().replace('.000Z', ''),
                media_ids: filters.media_ids,
            },
            null
        ).then(this.ab2obj)
            .then((res: any) => {
                if (isGetSuccess(res)) {
                    return res;
                } else if (isGetError(res)) {
                    throw new Error('Invalid get_declaration_csa: ' + JSON.stringify(res.results));
                }
                throw new Error('New Route: Invalid response from backend');
            });
    }
    // converts an object to an array buffer
    private obj2ab(obj: any): ArrayBuffer {
        return (new TextEncoder().encode(JSON.stringify(obj))).buffer;
    }

    // converts an array buffer to object
    private ab2obj(from: ArrayBuffer): any {
        const json = new TextDecoder().decode(new Uint8Array(from));

        return JSON.parse(json);
    }
}